/**
 * Aba "Leitura Estratégica (IA)" da ficha da cliente, idêntica à do legado (`admin.html:2775-2790`,
 * `6166-6342`; linhas 222 a 225 do inventário, e a trava de botão da linha 179).
 *
 * "Informações complementares", as abas consideradas, o estado do consentimento da cliente (só
 * lido), o botão que pede a análise à Edge Function `analisar-cliente-ia` e o texto devolvido.
 *
 * TRÊS REGRAS DE IA, e nenhuma é enfeite:
 *   · o botão só destrava com autorização da cliente EM VIGOR, e a Edge Function confere de novo
 *     no servidor (`6284-6288`); botão de tela não protege ninguém de chamada direta;
 *   · nada aqui registra consentimento: quem autoriza é a cliente, no portal dela (`2782-2785`);
 *   · a saída leva a marca de sugestão (ACRÉSCIMO pedido pelo principal, que o legado não tinha):
 *     a análise organiza, e quem decide é a consultora.
 *
 * Território da frente A3. A moldura importa esta aba pelo nome `AbaAnaliseIa`.
 */

// Estado e memória de função do React.
import { useCallback, useState } from 'react';
// As peças do Chassi: botão (na variante `ia`), texto longo, markdown e selo.
import { Botao, CampoTexto, Markdown, Selo, type TomDoSelo } from '@complexity/ui';
// O motivo real de uma falha de Edge Function, portado do legado.
import { motivoDoErroDeFuncao } from '@studiolu/dados';
// As leituras e o pedido de análise.
import { lerAnaliseIa, lerConsentimentoIa, pedirAnaliseIa } from '@studiolu/dados/areas/ficha-analise-ia';
import { lerAbasHabilitadas } from '@studiolu/dados/areas/ficha-abas-habilitadas';
import { lerQuestionarioGeral, lerQuestionarioPersonalidade } from '@studiolu/dados/areas/ficha-dossie';
// A fonte de dados do painel.
import { usePainel } from '../../../contexto.js';
// O contrato da aba, as chaves de parâmetro e o radar calculado no navegador.
import type { FichaAberta, PropsDaAba } from '../comum-ficha/tipos.js';
import { useCarregadorDaAba } from '../comum-ficha/carregador.js';
import { calcularPontosRaiz, chavesDeParametro } from '../comum-ficha/regras.js';
// As peças da A3.
import { textoAbasConsideradas } from './comum-a3/rotulos.js';
import { useEscopoEstavel } from './comum-a3/escopo.js';
import { useRecortePorPapel } from './comum-a3/papel.js';
import { AbaSemFicha } from './comum-a3/sem-ficha.js';

/** O estado do consentimento na tela: o selo e a frase ao lado dele (`admin.html:6206-6250`). */
interface Consentimento {
  readonly selo: string;
  readonly tom: TomDoSelo;
  readonly frase: string;
}

// "Conferindo", enquanto a leitura corre (`admin.html:6265-6266`). Selo cinza, o `badge-pendente`.
const CONFERINDO: Consentimento = { selo: 'Conferindo', tom: 'neutro', frase: 'Conferindo a autorização da cliente...' };

// A data do consentimento: dia, mês, ano, hora e minuto, sem segundos (`admin.html:6231-6233`).
function quandoDoConsentimento(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * A frase de vazio das abas consideradas nesta aba (`admin.html:6188`); a do Dossiê é outra.
 */
const SEM_ABAS =
  'Nenhuma aba de dados vinculada ao(s) produto(s) deste lead. A análise sairá só com informações complementares e arquivos enviados.';

export function AbaAnaliseIa({ ficha }: PropsDaAba) {
  // Sem a ficha aberta, a aba não consulta nada (ver `sem-ficha.tsx`).
  if (!ficha) return <AbaSemFicha aba="Leitura Estratégica (IA)" />;
  return <ConteudoAnaliseIa ficha={ficha} />;
}

function ConteudoAnaliseIa({ ficha }: { readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  const { somenteLeitura } = useRecortePorPapel();
  const escopo = useEscopoEstavel(ficha);
  const userId = ficha.cliente.user_id;
  // As chaves de parâmetro do lead, em texto para as dependências compararem por valor.
  const chaves = chavesDeParametro(ficha.lead?.produtos ?? []).join('|');
  const [informacoes, setInformacoes] = useState('');
  const [abasConsideradas, setAbasConsideradas] = useState('');
  const [consentimento, setConsentimento] = useState<Consentimento | null>(CONFERINDO);
  // O botão nasce travado, e só destrava com autorização em vigor (linha 179 e `6289`).
  const [travado, setTravado] = useState(true);
  const [rotuloDoBotao, setRotuloDoBotao] = useState('Criar análise');
  const [status, setStatus] = useState('');
  const [analise, setAnalise] = useState('');

  // A leitura do consentimento: escreve o estado na tela e devolve se está autorizado
  // (`carregarConsentimentoAnaliseIA`, `admin.html:6206-6250`).
  const conferirConsentimento = useCallback(async (): Promise<boolean> => {
    // Sem cliente, nada a mostrar, e o botão fica travado.
    if (!userId) {
      setConsentimento(null);
      return false;
    }
    const resultado = await lerConsentimentoIa(dados, userId);
    // Falha de rede devolve "não" de propósito: sem conferir, não libera (`6204-6205`).
    if (!resultado.ok) {
      console.error('Erro ao consultar o consentimento da cliente:', resultado.erro.mensagem);
      setConsentimento({
        selo: 'Não confirmado',
        tom: 'erro',
        frase: 'Não deu pra conferir a autorização da cliente agora. O botão fica travado até dar. Abra a aba de novo em instantes.',
      });
      return false;
    }
    const ultima = (resultado.dados ?? [])[0] ?? null;
    if (ultima && ultima.concedido === true) {
      setConsentimento({
        selo: 'Autorizado',
        tom: 'sucesso',
        frase: `A cliente autorizou o envio das fotos e respostas para a inteligência artificial em ${quandoDoConsentimento(ultima.registrado_em)}.`,
      });
      return true;
    }
    if (ultima && ultima.concedido === false) {
      setConsentimento({
        selo: 'Autorização retirada',
        tom: 'erro',
        frase: `A cliente retirou a autorização em ${quandoDoConsentimento(ultima.registrado_em)}. Sem ela, nada é enviado para a inteligência artificial. Só a própria cliente pode autorizar de novo, na aba "Análise por IA" do portal dela. A análise segue possível na mão.`,
      });
      return false;
    }
    setConsentimento({
      selo: 'Sem resposta',
      tom: 'neutro',
      frase:
        'A cliente ainda não respondeu sobre o envio das fotos e respostas para a inteligência artificial. Peça pra ela abrir a aba "Análise por IA" no portal e decidir. Você não pode responder por ela.',
    });
    return false;
  }, [dados, userId]);

  // As abas habilitadas pelos produtos do lead; a falha vale conjunto vazio, como o `data || []`.
  const lerAbas = useCallback(async (): Promise<Set<string>> => {
    const resultado = await lerAbasHabilitadas(dados, chaves ? chaves.split('|') : []);
    return new Set(resultado.ok ? resultado.dados.map((linha) => linha.aba) : []);
  }, [dados, chaves]);

  // A carga da aba (`carregarAnaliseIA`, `admin.html:6252-6290`), na ordem do legado, pelo contrato
  // de carga: a moldura a roda antes de mostrar a aba, e de novo a cada toque no botão dela. Ela
  // limpa tudo, e é por isso que nada desta aba sobrevive à troca de aba: quem apaga é o carregador.
  const carregar = useCallback(async () => {
    // Limpa tudo antes, para a aba não mostrar a resposta da cliente anterior (`6263-6264`).
    setTravado(true);
    setStatus('');
    setAnalise('');
    setInformacoes('');
    setConsentimento(CONFERINDO);
    const abas = await lerAbas();
    setAbasConsideradas(textoAbasConsideradas(abas) ?? SEM_ABAS);
    const resultado = await lerAnaliseIa(dados, escopo);
    if (!resultado.ok) console.error('Erro ao carregar análise IA:', resultado.erro.mensagem);
    const linha = resultado.ok ? resultado.dados : null;
    if (linha) {
      setInformacoes(linha.informacoes_complementares || '');
      setAnalise(linha.analise || '');
      setStatus(linha.atualizado_em ? `Última análise gerada em ${new Date(linha.atualizado_em).toLocaleString('pt-BR')}` : '');
      setRotuloDoBotao('Refazer análise');
    } else {
      setRotuloDoBotao('Criar análise');
    }
    const autorizado = await conferirConsentimento();
    setTravado(!autorizado);
  }, [dados, escopo, lerAbas, conferirConsentimento]);
  useCarregadorDaAba(carregar);

  // "Criar análise" / "Refazer análise" (`admin.html:6292-6342`).
  const gerar = async () => {
    setTravado(true);
    setStatus('Gerando análise, aguarde...');
    // O radar calculado no navegador, como o legado manda (`6298-6300`); leitura que falha vale nula.
    const geral = await lerQuestionarioGeral(dados, escopo);
    const personalidade = await lerQuestionarioPersonalidade(dados, escopo);
    const linhaGeral = geral.ok && geral.dados ? { ...geral.dados } : null;
    const linhaPersonalidade = personalidade.ok && personalidade.dados ? { ...personalidade.dados } : null;
    const pontos = calcularPontosRaiz(linhaGeral, linhaPersonalidade);
    const abas = await lerAbas();
    const resultado = await pedirAnaliseIa(dados, {
      user_id: userId,
      lead_id: ficha.lead ? ficha.lead.id : null,
      informacoes_complementares: informacoes.trim(),
      pontos_estilo_raiz: pontos,
      abas_habilitadas: [...abas],
    });
    // Reconfere o consentimento antes de destravar: a cliente pode ter retirado no meio (`6320-6323`).
    setTravado(!(await conferirConsentimento()));
    if (!resultado.ok) {
      // O motivo real vem no corpo da resposta, e não no "non-2xx" genérico (`6325-6333`).
      const motivo = motivoDoErroDeFuncao({
        mensagem: resultado.erro.mensagem,
        status: resultado.erro.status,
        corpo: resultado.erro.corpo,
      });
      setStatus('Erro ao gerar análise: ' + motivo);
      return;
    }
    // 200 com `{ error }` é regra de negócio da função (`6335-6338`).
    if (resultado.dados?.error) {
      setStatus('Erro ao gerar análise: ' + resultado.dados.error);
      return;
    }
    setAnalise(resultado.dados?.analise || '');
    setStatus('Análise gerada agora.');
    setRotuloDoBotao('Refazer análise');
  };

  return (
    <section className="flex flex-col gap-cx-3">
      <h3 className="text-cx-base font-semibold text-texto">Leitura Estratégica (IA)</h3>
      {/* Consulta: campo desabilitado (`admin.html:3394`). */}
      <CampoTexto
        rotulo="Informações complementares"
        linhas={3}
        placeholder="Qualquer contexto que não esteja nos questionários: observações da reunião, contexto da validação, etc."
        value={informacoes}
        disabled={somenteLeitura}
        onChange={(evento) => setInformacoes(evento.target.value)}
      />
      <p data-testid="analise-abas-consideradas" className="text-cx-xs text-texto-suave">
        {abasConsideradas}
      </p>
      {/* O estado do consentimento: só mostra, nunca registra (`admin.html:2782-2786`). */}
      <div data-testid="analise-consentimento" className="flex min-h-[1.25rem] flex-wrap items-start gap-cx-2 text-cx-sm text-texto-suave">
        {consentimento && (
          <>
            <Selo tom={consentimento.tom}>{consentimento.selo}</Selo>
            <span className="min-w-0 flex-1">{consentimento.frase}</span>
          </>
        )}
      </div>
      {/* Consulta não vê botão de ação (`admin.html:3395`). A variante `ia` desenha o símbolo. */}
      {!somenteLeitura && (
        <div>
          <Botao variante="ia" disabled={travado} onClick={() => void gerar()}>
            {rotuloDoBotao}
          </Botao>
        </div>
      )}
      <p role="status" data-testid="status-analise-ia" className="min-h-[1.25rem] text-cx-sm text-texto-suave">
        {status}
      </p>
      {analise && (
        <div data-testid="resultado-analise-ia" className="flex flex-col gap-cx-2">
          {/* A marca de sugestão (acréscimo): a saída é da IA, e a decisão é da consultora. */}
          <div>
            <Selo tom="info">Sugestão gerada por inteligência artificial</Selo>
          </div>
          {/* O markdown do Chassi, no lugar de `marked` com `DOMPurify` (`6174`): ele cobre um
              subconjunto (títulos, listas com "-", negrito, link, tabela), divergência listada. */}
          <Markdown texto={analise} />
        </div>
      )}
    </section>
  );
}
